import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { format, isValid, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

import ProfileForm, { IProfileValues } from "./Form/profileForm";
import SecurityCard from "./components/SecurityCard";
import ProfileSummaryCard from "./components/ProfileSummaryCard";
import ProfilePlanCard from "./components/ProfilePlanCard";
import ProfileMobileMenu, { ProfileSubView } from "./components/ProfileMobileMenu";
import { getUserProfile, IUpdateUserAPI, putUserProfile, UserProfileResponse } from "../../../services/profileService";
import { handlePromise } from "../../../utils/handlePromisse";
import { removeEmptyAttributes } from "../../../utils";
import { useAuth } from "../../../contexts/auth2";
import { isFreePlan } from "../../../services/userService";
import { MessageDialog } from "../../../components/message-dialog";
import { SimpleSnackbar } from "../../../components/snackbar";
import PageTitle from "../../../components/PageTitle";

const formatEndPlan = (datEndPlan?: string): string => {
  if (!datEndPlan) return "";
  const parsed = parseISO(datEndPlan);
  return isValid(parsed) ? format(parsed, "dd 'de' MMMM", { locale: ptBR }) : "";
};

const SUBVIEW_TITLE: Record<ProfileSubView, string> = {
  dados: "Dados pessoais",
  seguranca: "Segurança",
};

export const ProfilePage: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [profile, setProfile] = useState<UserProfileResponse>({} as UserProfileResponse);
  const [savedOpen, setSavedOpen] = useState(false);
  const [mobileView, setMobileView] = useState<ProfileSubView | "menu">("menu");

  const fetchProfile = useCallback(async () => {
    const [result, error] = await handlePromise(getUserProfile(user.idUser));
    if (!error) setProfile(result.data);
  }, [user.idUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const saveProfileChanges = async (idUser: number, entries: Partial<IProfileValues>, setReload: Function) => {
    const { fullName, phone, occupationArea, cep, address, city, state, district, number } = entries;
    const updateObj: IUpdateUserAPI = {
      phone_User: phone,
      occupation_User: occupationArea,
      name_User: fullName,
      address: { zipcode: cep, street: address, city, state, neighbourhood: district, number },
    };

    const [, error] = await handlePromise(putUserProfile(idUser, removeEmptyAttributes(updateObj)));
    if (error) {
      setSnackbarMessage("Erro ao atualizar dados");
      setShowSnackbar(true);
      return;
    }
    setSavedOpen(true);
    setReload(true);
  };

  const planIsFree = isFreePlan(profile.planName);
  const planDisplayName = planIsFree ? "Gratuito" : profile.planName ?? "";
  const renewsAt = formatEndPlan(profile.dat_end_plan);

  const summaryCard = (
    <ProfileSummaryCard
      name={profile.name_User || "—"}
      occupation={profile.occupation_User || ""}
      email={profile.email_User || user.email || ""}
    />
  );
  const planCard = (
    <ProfilePlanCard planName={planDisplayName} renewsAt={renewsAt} isFree={planIsFree} onManage={() => navigate("/dashboard/pricing")} />
  );
  const personalForm = <ProfileForm handleSubmitProp={saveProfileChanges} />;

  const subviews: Record<ProfileSubView, ReactNode> = {
    dados: personalForm,
    seguranca: <SecurityCard />,
  };

  return (
    <>
      <PageTitle title="Meu perfil" />
      <MessageDialog open={savedOpen} message="Dados alterados com sucesso!" onConfirm={() => setSavedOpen(false)} confirmButtonText="OK" />
      <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

      <Box sx={{ width: "100%", p: { xs: "18px 16px 24px", md: 4 } }}>
        {planIsFree && (
          <Box sx={{ mb: 2 }}>
            <Link to="/dashboard/pricing" style={{ color: theme.palette.primary.main }}>FAZER UPGRADE</Link>
          </Box>
        )}

        {/* Mobile: menu + sub-telas (padrão v4). Desktop: 2 colunas. */}
        {isMobile ? (
          mobileView === "menu" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {summaryCard}
              {planCard}
              <ProfileMobileMenu onSelect={setMobileView} />
            </Box>
          ) : (
            <Box>
              <Button
                startIcon={<ChevronLeft />}
                onClick={() => setMobileView("menu")}
                sx={{ mb: 2, color: "text.secondary", px: 0, minWidth: 0, "&:hover": { backgroundColor: "transparent", color: "text.primary" } }}
              >
                Perfil
              </Button>
              <Typography sx={{ fontFamily: theme.typography.fontFamilyDisplay, fontSize: 24, color: "text.primary", mb: 2 }}>
                {SUBVIEW_TITLE[mobileView]}
              </Typography>
              {subviews[mobileView]}
            </Box>
          )
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: { md: "320px 1fr" }, gap: 3, alignItems: "start" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {summaryCard}
              {planCard}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {personalForm}
              <SecurityCard />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
